import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { useSubmitPurchaseForm } from "../../lib/queries/queriesAndMutations";
import CurrencyConverter from "../../utils/CurrencyUtil";
import {
  cn,
  handleErrorApi,
  removeLoSt,
  toLocaleStringFormatter,
  unixToPersianDay,
  wageCalculator,
} from "../../utils/utils";
import Loader from "../shared/Loader";
import ShowMoreText from "../shared/show-more";
import CustomPayemntNumberInput from "../ui/payment/CustomPayemntNumberInput";
import CustomPaymentInput from "../ui/payment/CustomPaymentInput";

export default function PaymentForm({ formData, wage_percent, min_wage, max_wage }) {
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isNameRequired = formData?.is_payer_name_mandatory;
  const isEmailRequired = formData?.is_payer_email_mandatory;
  const isNationalCodeRequired = formData?.is_national_code_mandatory;
  const isPostalCodeRequired = formData?.is_postal_code_mandatory;
  const isPhoneRequired = formData?.is_payer_phone_mandatory;
  const isAddressRequired = formData?.is_payer_address_mandatory;

  const schema = createPaymentFormSchema({
    isNameRequired,
    isEmailRequired,
    isNationalCodeRequired,
    isPostalCodeRequired,
    isPhoneRequired,
    isAddressRequired,
  });
  const {
    watch,
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    values: formData,
    resolver: zodResolver(schema),
  });

  const { mutateAsync: submitPurchase, isPending } = useSubmitPurchaseForm();

  const formAmount = watch("form_amount");

  const formattedAmount = formData?.fixed_amount
    ? removeLoSt(formAmount)
    : removeLoSt(removeLoSt(formAmount) * 10);

  const wageAmount = !formData?.is_wage_on_owner
    ? wageCalculator(formattedAmount, wage_percent, min_wage, max_wage)
    : "0";

  // const disableForTargetAmount = formData.has_target_amount
  //   ? formattedAmount + formData?.paid_amount > formData?.target_amount ?? 0
  //   : false;
  const isWageOnOwner = Boolean(formData?.is_wage_on_owner);

  const wageInTomans = Math.ceil(Number(removeLoSt(wageAmount ?? "0")) / 10);

  const displayedWage = isWageOnOwner ? "0" : toLocaleStringFormatter(wageInTomans);

  const renderWageInfo = () => (
    <div className="col-span-6 flex items-center justify-between">
      <p className="flex items-center gap-1">
        کارمزد:
        <span className="text-[14px] text-primary-90 font-bold">
          {isWageOnOwner ? "(برعهده صاحب درگاه است)" : "(برعهده پرداخت‌کننده است)"}
        </span>
      </p>

      <div className="flex gap-2">
        <span className="h-fit rounded-md bg-black px-[5px] text-[12px] text-white">
          {wage_percent ?? 0}%
        </span>

        <p>
          <span className="payment-link-text-value">{displayedWage}</span>
          {" تومان"}
        </p>
        {/* <div className="flex gap-2">
          <p className="bg-black text-white px-[5px]  text-[12px] h-fit rounded-md">{wage_percent} %</p>
          <p>
            <span className="payment-link-text-value">{formData?.is_wage_on_owner ? "0" : wageAmount}</span>
            تومان
          </p>
        </div> */}
      </div>
    </div>
  );
  const renderTotalAmount = (wageAmount) => {
    const amount = Number(formattedAmount);
    const totalwage = Number(removeLoSt(wageAmount));

    const total = toLocaleStringFormatter(Math.ceil((amount + Math.ceil(totalwage)) / 10));
    return (
      <div className="flex justify-between items-center  col-span-6  ">
        <p className="flex gap-1 items-center">جمع کل</p>

        <p className="payment-link-text-value">{total} تومان</p>
      </div>
    );
  };

  const processForm = async (data) => {
    setIsSubmitting(true);
    const process = {
      form_id: formData?.id,
      amount: formData?.fixed_amount ? removeLoSt(formData?.form_amount) : removeLoSt(data?.form_amount) * 10,
      payer_name: data?.payer_name,
      payer_mobile: data?.payer_mobile,
      payer_mail: data?.payer_mail,
      payer_address: data?.payer_address,
      payer_national_code: data?.payer_national_code,
      payer_postal_code: data?.payer_postal_code,
      payer_description: data?.payer_description,
    };

    //console.log(process);
    const processedData = Object.keys(process).reduce((acc, key) => {
      acc[key] = process[key] === "" || process[key] === undefined ? null : process[key];
      return acc;
    }, {});

    try {
      const response = await submitPurchase(processedData);

      if (response.status === 200 || response.status === 201) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      handleErrorApi(error);
    }
    setIsSubmitting(false);
  };

  const amountRef = useRef(null);

  const handleFocus = () => {
    if (amountRef.current) {
      amountRef.current.focus();
    }
  };

  useEffect(() => {
    // Automatically focus the input when the component mounts
    handleFocus();
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="py-16">
        <Loader />
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(processForm)}
      className={`grid grid-cols-6 w-full gap-6   md:min-w-[700px]   p-1  `}
    >
      <div className="flex flex-col gap-3   break-normal  mt-6  col-span-6  " dir="rtl">
        {/* <label> توضیحات:</label> */}
        <ShowMoreText
          lines={3}
          more="نمایش بیشتر"
          less="نمایش کمتر"
          className="bg-white break-words "
          anchorClass="text-dark cursor-pointer text-[10px] "
          expanded={false}
          width={0}
          truncatedEndingComponent="... "
        >
          {formData?.description || ""}
        </ShowMoreText>
      </div>

      {!formData?.fixed_amount ? (
        <CustomPayemntNumberInput
          control={control}
          name="form_amount"
          disabled={formData?.fixed_amount}
          label="مبلغ (تومان)*"
          placeholder="مقدار مبلغ (تومان)"
          type="number"
          className="col-span-6 xl:col-span-6 "
          maxValue={import.meta.env.VITE_PAYMENT_FORM_MAX_MONEY}
          amountRef={amountRef}
        />
      ) : (
        <div className="w-full text-center col-span-6 ">
          <span className="text-[48px] font-semibold">
            {toLocaleStringFormatter(formData?.form_amount / 10)}
          </span>{" "}
          تومان
        </div>
      )}

      <div className="text-[16px] text-[#999999] font-normal  col-span-6  mt-2">
        {formAmount ? <CurrencyConverter num={(formattedAmount / 10).toFixed(0)} /> : "0"}{" "}
      </div>

      <div className="w-full flex flex-col rounded-md gap-4 md:mt-4    col-span-6">
        <div className="w-full flex gap-6  px-4 h-full   ">
          {formData?.has_target_amount && (
            <>
              <div className="payment-form-detail-card">
                <p className="text-primary-100">
                  {formData?.paid_amount ? toLocaleStringFormatter(formData?.paid_amount / 10) : "0"} تومان
                </p>
                <p className="payment-link-text-value">
                  {toLocaleStringFormatter(formData?.target_amount / 10)} تومان
                </p>
              </div>
              <div className="payment-form-detail-card">
                <p className="text-primary-100">% {formData?.paid_amount / formData?.target_amount}</p>
                <p className="payment-link-text-value">تامین شده</p>
              </div>
            </>
          )}
          {formData?.is_expendable && (
            <div className="payment-form-detail-card">
              <p className="text-primary-100">پایان مهلت</p>
              <p className="payment-link-text-value">{unixToPersianDay(formData?.expire_time)}</p>
            </div>
          )}
        </div>

        <div className={`grid grid-cols-6  gap-6 col-span-6   px-2   `}>
          {!formData?.is_wage_on_owner && (
            <div className=" col-span-6 rounded-[24px] p-[14px] flex flex-col  gap-4 bg-secondary-10">
              {renderWageInfo()}
              {renderTotalAmount(wageAmount)}
            </div>
          )}

          {formData?.display_payer_name && (
            <CustomPaymentInput
              control={control}
              required={formData?.is_payer_name_mandatory}
              className="col-span-6 lg:col-span-3 xl:col-sapn-2 "
              name="payer_name"
              placeholder="نام پرداخت‌کننده"
              type="text"
              errors={errors.payer_name}
            />
          )}

          {formData?.display_payer_email && (
            <CustomPaymentInput
              control={control}
              required={formData?.is_payer_email_mandatory}
              className="col-span-6 lg:col-span-3 xl:col-sapn-2 "
              name="payer_mail"
              placeholder=" ایمیل پرداخت‌کننده "
              type="text"
              errors={errors.payer_mail}
            />
          )}
          {formData?.display_national_code && (
            <CustomPaymentInput
              control={control}
              required={formData?.is_national_code_mandatory}
              name="payer_national_code"
              className="col-span-6 lg:col-span-3 xl:col-sapn-2 "
              placeholder="  کدملی پرداخت‌کننده  "
              type="number"
              errors={errors.payer_national_code}
            />
          )}
          {formData?.display_postal_code && (
            <CustomPaymentInput
              control={control}
              required={formData?.is_postal_code_mandatory}
              className="col-span-6 lg:col-span-3 xl:col-sapn-2 "
              name="payer_postal_code"
              placeholder="  کدپستی پرداخت‌کننده  "
              type="number"
              errors={errors.payer_postal_code}
            />
          )}
          {formData?.display_payer_phone && (
            <CustomPaymentInput
              control={control}
              required={formData?.is_payer_phone_mandatory}
              className="col-span-6 lg:col-span-3 xl:col-sapn-2 "
              name="payer_mobile"
              placeholder="شماره همراه پرداخت‌کننده "
              type="number"
              errors={errors.payer_mobile}
            />
          )}

          {formData?.display_payer_address && (
            <CustomPaymentInput
              control={control}
              required={formData?.is_payer_address_mandatory}
              className="col-span-6 lg:col-span-6 xl:col-sapn-6 "
              name="payer_address"
              placeholder="آدرس پرداخت‌کننده  "
              type="text"
              errors={errors.payer_address}
            />
          )}

          <TextArea
            register={register}
            name="payer_description"
            label="توضیحات پرداخت"
            placeholder=""
            type="text"
            errors={errors.payer_description}
            className="col-span-6 lg:col-span-6 xl:col-sapn-6 "
          />

          {/* <CustomPaymentInput
            register={register}
            className={"col-span-6 lg:col-span-6 xl:col-sapn-6 "}
            name={"payer_description"}
            placeholder={"توضیحات"}
            type={"text"}
            errors={errors.payer_description}
            max={500}
          /> */}

          {/* {formData?.has_target_amount && disableForTargetAmount && (
            <p className="text-error-90 col-span-6 text-[14px] bg-error-10  p-2 rounded-lg ">
              مجموع مبالغ پرداختی بیش از سقف مجاز مبلغ هدف است
            </p>
          )} */}

          <hr className="col-span-6 mt-8" />
          <button
            className="bg-dark hover:bg-primary-80 text-white col-span-6 flex-center disabled:bg-secondary-30 min-h-[52px] disabled:pointer-events-none rounded-lg p-1` "
            type={isPending ? "button" : "submit"}
            disabled={formattedAmount < 1000 || isPending || isSubmitting}
            onClick={(e) => {
              if (formattedAmount < 1000 || isPending) {
                e.preventDefault();
                return;
              }
            }}
          >
            {isPending ? (
              <div className="w-8 h-8">
                <Loader />
              </div>
            ) : (
              "پرداخت"
            )}
          </button>
        </div>
      </div>
    </form>
  );
}

const createPaymentFormSchema = ({
  isNameRequired = false,
  isEmailRequired = false,
  isNationalCodeRequired = false,
  isPostalCodeRequired = true,
  isPhoneRequired = false,
  isAddressRequired = false,
}) => {
  return z.object({
    form_amount: z.union([z.string(), z.number()]),
    payer_name: isNameRequired
      ? z.string().min(1, { message: "نام پرداخت کننده الزامی است" })
      : z.string().optional().nullable(),

    payer_mobile: isPhoneRequired
      ? z
          .string()
          .min(1, { message: "شماره همراه الزامی است" })
          .length(11, { message: "شماره تلفن باید 11 رقمی باشد" })
      : z.string().optional().nullable(),

    payer_postal_code: isPostalCodeRequired
      ? z
          .string()
          .min(1, { message: "کدپستی الزامی است" })
          .length(10, { message: "کدپستی باید ده رقمی باشد" })
      : z.string().optional().nullable(),

    payer_national_code: isNationalCodeRequired
      ? z
          .string()
          .min(1, { message: "کد ملی الزامی است" })
          .length(10, { message: "کد ملی باید ده رقمی باشد" })
      : z.string().optional().nullable(),

    payer_mail: isEmailRequired
      ? z.string().min(1, { message: "ایمیل الزامی است" }).email({ message: "فرمت ایمیل صحیح نمی باشد" })
      : z.string().optional().nullable(),

    payer_address: isAddressRequired
      ? z.string().min(1, { message: "آدرس الزامی است" })
      : z.string().optional().nullable(),
    payer_description: z.string().max(5000, { message: "توصیحات طولانی است" }),
  });
};

const TextArea = (props) => {
  const { register, name, label, type, onchange, errors, placeholder, className, disabled } = props;

  return (
    <div className={cn("text-[14px] text-neutral-50", className)}>
      <label htmlFor="" className="text-[14px] text-neutral-50">
        {label}
      </label>
      <textarea
        className="min-h-[52px] max-h-[82px] no-scrollbar p-4 my-1 outline-none rounded-v-md  w-full border-2 border-neutral-20 disabled:bg-neutral-10 disabled:border-none"
        {...register(name, { required: true })}
        name={name}
        type={type}
        disabled={disabled}
        onKeyUp={onchange}
        placeholder={placeholder}
        onWheel={(e) => e.target.blur()}
      />
      {errors?.message && <span className="text-red-500 text-sm">{errors?.message}</span>}
    </div>
  );
};
